/**
 * Shared utilities for slot components
 *
 * This file contains common utility functions and components used across
 * different slot types (insight, report, content refresh, content outline, etc.)
 */

import React from 'react';
import { motion } from 'motion/react';
import { ArrowUpRight, Eye } from 'lucide-react';
import { Button } from '@strivelabs/ui-core/v2';
import type { AgentStep } from '@strivelabs/ui-blocks';
import { SLOT_ANIMATION, SLOT_CONTAINER_CLASSES } from '@/constants/reports';
import { ProcessingStatus } from '@/hooks/useKeyValOperations';
import type { ChatCTABlock, InsightCardBlock, PlanCardBlock, PlanCardContent, ReportBlock } from '@/types/ui-blocks';

/**
 * Shared slot container component with consistent animation and styling.
 * Wraps content in a motion.div with standard slot appearance using constants.
 *
 * @param children - Content to render inside the slot
 * @param slotRef - Optional ref to attach to the container
 */
export function SlotContainer({
  children,
  slotRef,
}: {
  children: React.ReactNode;
  slotRef?: React.RefObject<HTMLDivElement>;
}) {
  return (
    <motion.div
      ref={slotRef}
      {...SLOT_ANIMATION}
      className={SLOT_CONTAINER_CLASSES}
    >
      {children}
    </motion.div>
  );
}

/**
 * Parses keywords from various formats (array or comma-separated string) into a string array
 *
 * @param keywords - Keywords as array or comma-separated string
 * @returns Array of trimmed, non-empty keyword strings
 */
export function parseKeywords(keywords: unknown): string[] {
  if (Array.isArray(keywords)) {
    return keywords;
  }
  if (typeof keywords === 'string') {
    return keywords.split(',').map(k => k.trim()).filter(k => k.length > 0);
  }
  return [];
}

/**
 * Renders a simplified action indicator for content slots (refresh/outline).
 * Completed/review rows show a "View Details" link that navigates to the agent page.
 * In-progress rows show a spinner. Pending/failed rows show text-only status.
 *
 * @param status - Current processing status of the task
 * @param onNavigateToAgent - Callback to navigate to the full agent page
 */
export function getSlotActionButton({
  status,
  onNavigateToAgent,
}: {
  status: ProcessingStatus;
  onNavigateToAgent: () => void;
}) {
  if (status === ProcessingStatus.COMPLETED) {
    return (
      <Button variant="borderless" size="xs" onClick={onNavigateToAgent}>
        View Details
        <ArrowUpRight className="w-4 h-4" />
      </Button>
    );
  }
  if (status === ProcessingStatus.WAITING_FOR_APPROVAL) {
    return (
      <Button variant="borderless" size="xs" onClick={onNavigateToAgent}>
        Review Now
        <Eye className="w-4 h-4" />
      </Button>
    );
  }
  if (status === ProcessingStatus.IN_PROGRESS) {
    return (
      <span className="inline-flex items-center gap-1.5 text-sm text-amber-600">
        <span className="w-2 h-2 rounded-full bg-amber-500" />
        Processing
      </span>
    );
  }
  if (status === ProcessingStatus.FAILED) {
    return (
      <span className="w-full text-center text-sm text-red-600">-</span>
    );
  }
  return (
    <span className="flex items-center gap-1 text-sm text-gray-600">Pending</span>
  );
}

// Generic PDF download helpers live in utils; re-exported here so existing
// slot consumers keep their import path.
export { toPdfFilename, downloadPdfFromUrl } from '@/utils/pdf';

/**
 * Type guard to check if a block is an InsightCardBlock.
 *
 * Uses the more robust check for 'block_elements' property rather than the optional 'type' property,
 * since the 'type' field is optional in the InsightCardBlock type definition.
 *
 * @param block - Block to check
 * @returns True if the block is an InsightCardBlock
 */
export const isInsightCardBlock = (block: ReportBlock | unknown): block is InsightCardBlock =>
  typeof block === 'object' &&
  block !== null &&
  'block_elements' in block &&
  Array.isArray(block.block_elements);

/** Type guard for a plan_card report block. */
export const isPlanCardBlock = (block: ReportBlock): block is PlanCardBlock =>
  'type' in block && block.type === 'plan_card';

/** Type guard for a top-level chat_cta report block. */
export const isChatCtaBlock = (block: ReportBlock): block is ChatCTABlock =>
  'type' in block && block.type === 'chat_cta';

/** Map PlanCardContent steps → AgentStep[] for AgentThoughtsCard. */
export const mapPlanCardStepsToAgentSteps = (steps: PlanCardContent['steps']): AgentStep[] =>
  steps.map(step => ({
    id: step.task_id,
    title: step.title,
    status: step.status,
    content: { type: 'summary' as const, body: step.description ?? '' },
  }));
